import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { businessDeal, timeManagement } from '../assets/images';
import { Check, ArrowRight } from 'lucide-react';
import useFormSubmit from '../hooks/useFormSubmit';

const API_URL = (import.meta.env.VITE_API_URL || 'https://forms.financesunshine.com').replace(/\/+$/, '');

const ConcessionnairesPage = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    businessName: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    contactName: '',
    contactPhone: '',
    contactEmail: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const [honeypot, setHoneypot] = useState('');
  const { submitForm, isSubmitting, isSuccess, isError } = useFormSubmit(`${API_URL}/dealer`);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await submitForm({ ...formData, _hp: honeypot });
    if (result.success) {
      setFormData({ businessName: '', phone: '', website: '', address: '', city: '', province: '', postalCode: '', contactName: '', contactPhone: '', contactEmail: '' });
    }
  };

  const features = (t('dealers.features', { returnObjects: true }) as string[]) || [];

  return (
    <div className="pt-24">
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6 md:px-12 mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl font-bold text-primary-950 mb-6">{t('dealers.title')}</h1>
              <p className="text-lg text-gray-600 mb-8">
              {t('dealers.intro')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
              {t('dealers.offering')}
              </p>
              <p className="text-lg text-gray-600 mb-8">
              {t('dealers.adaptability')}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => document.getElementById('accreditation-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center px-8 py-3.5 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                >
                  <span>{t('dealers.accreditation')}</span>
                  <ArrowRight size={20} className="ml-2" />
                </button>
              </div>
            </div>
            <div className="relative">
              <img 
                src={businessDeal}
                alt="Business handshake illustration"
                className="w-full h-auto"
              />
            </div>
          </div>
          <div className="mt-32">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {features.map((feature: any, index: number) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <Check className="flex-shrink-0 mt-1 text-black" size={20} />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div id="accreditation-form" className="bg-white rounded-xl shadow-sm p-8 mb-12">
                <h2 className="text-2xl font-bold text-primary-950 mb-6">{t('dealers.accreditation')}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                    <label>
                      Ne pas remplir / Leave empty
                      <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </label>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">{t('dealers.form.dealerInfo')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="businessName"
                        required
                        maxLength={200}
                        placeholder={t('dealers.form.businessName')}
                        value={formData.businessName}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <input
                        type="tel"
                        name="phone"
                        placeholder={t('contact.phone')}
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <input
                      type="url"
                      name="website"
                      placeholder={t('dealers.form.website')}
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    />
                    <input
                      type="text"
                      name="address"
                      placeholder={t('contact.address')}
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        name="city"
                        placeholder={t('dealers.form.city')}
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <input
                        type="text"
                        name="province"
                        placeholder={t('dealers.form.province')}
                        value={formData.province}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <input
                        type="text"
                        name="postalCode"
                        placeholder={t('dealers.form.postalCode')}
                        value={formData.postalCode}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">{t('dealers.form.contactPerson')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="contactName"
                        required
                        maxLength={200}
                        placeholder={t('contact.name')}
                        value={formData.contactName}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                      <input
                        type="tel"
                        name="contactPhone"
                        placeholder={t('contact.phone')}
                        value={formData.contactPhone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <input
                      type="email"
                      name="contactEmail"
                      required
                      maxLength={254}
                      placeholder={t('contact.email')}
                      value={formData.contactEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>

                  {isSuccess && (
                    <p role="status" className="p-4 rounded-md bg-green-50 text-green-800">{t('dealers.form.success')}</p>
                  )}
                  {isError && (
                    <p role="alert" className="p-4 rounded-md bg-red-50 text-red-800">{t('dealers.form.error')}</p>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-3 bg-black hover:bg-gray-900 text-white font-medium rounded-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-60"
                  >
                    {isSubmitting ? t('dealers.form.sending') : t('dealers.form.submit')}
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-8 flex items-center gap-8">
                <div className="flex-1 space-y-4">
                  <h2 className="text-2xl font-bold text-primary-950 mb-6">{t('dealers.easyFast')}</h2>
                  <p className="text-lg text-gray-600">
                    {t('dealers.process')}
                  </p>
                </div>
                <img 
                  src={timeManagement}
                  alt="Time management illustration"
                  className="w-64 h-auto hidden md:block"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConcessionnairesPage;